import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Typography } from '@interstate/components/Typography';
import { Modal } from '@interstate/components/Modal';
import { CheckBox } from '@interstate/components/CheckBox';
import { Switch } from '@interstate/components/Switch';
import { Box } from '@interstate/components/Box';
import { ArrowLongRightIcon } from '@interstate/components/Icons/ArrowLongRightIcon';

import type { ItemRetentionConfirmationModalProps } from './ItemRetentionConfirmationModal.types';
import {
    StyledVehicleComparison,
    StyledArrowSeparator,
    StyledDealDetailsHeader,
    StyledDealDetailsSeparator,
    StyledCategorySection,
    StyledCategoryHeader,
    StyledItemRow,
    StyledItemLabel,
    StyledKeepCheckboxWrapper,
    StyledModalBody,
    buildRetentionModalCss
} from './ItemRetentionConfirmationModal.styles';
import { VehicleCard } from './VehicleCard';
import { useItemRetentionSelections } from './useItemRetentionSelections';
import { middlewareMgr } from '../../store';
import {
    createDealDetailsConfirmationEvent,
    createDealDetailsRetentionToggleEvent,
    createProceedAndOpenProgramsEvent
} from '../../analytics';

export const ItemRetentionConfirmationModal: React.FC<ItemRetentionConfirmationModalProps> = ({
    show,
    currentVehicle,
    newVehicle,
    onProceed,
    onProceedAndOpenPrograms,
    onCancel,
    allowDismiss = true,
    modalId = 'item-retention-modal',
    proceedLabel = 'Proceed'
}) => {
    const dispatch = useDispatch();
    const { categories, selections, allChecked, handleCheckboxChange, handleKeepAllToggle, buildRetentionDecisions } =
        useItemRetentionSelections(show);

    const handleProceed = useCallback(() => {
        // dc-mv-318: Deal Details Confirmation ("Proceed") button click
        dispatch(middlewareMgr.hostActions.forwardCDLEvent(createDealDetailsConfirmationEvent().payload));
        onProceed(buildRetentionDecisions());
    }, [dispatch, onProceed, buildRetentionDecisions]);

    const handleProceedAndOpenPrograms = useCallback(() => {
        // dc-mv-320: "Proceed & Open Programs" button click
        dispatch(middlewareMgr.hostActions.forwardCDLEvent(createProceedAndOpenProgramsEvent().payload));
        onProceedAndOpenPrograms(buildRetentionDecisions());
    }, [dispatch, onProceedAndOpenPrograms, buildRetentionDecisions]);

    const handleRetentionToggle = useCallback(() => {
        // dc-mv-319: "Keep all" / "Remove all" deal details toggle click.
        // `allChecked` reflects the pre-click state, so the resulting state is its negation.
        dispatch(middlewareMgr.hostActions.forwardCDLEvent(createDealDetailsRetentionToggleEvent(!allChecked).payload));
        handleKeepAllToggle();
    }, [dispatch, allChecked, handleKeepAllToggle]);

    const cancelOption = { buttonStyle: 'tertiary' as const, action: onCancel, label: 'Cancel' };
    const programsOption = {
        buttonStyle: 'secondary' as const,
        action: handleProceedAndOpenPrograms,
        label: `${proceedLabel} & Open Programs`
    };

    return (
        <>
            <style>{buildRetentionModalCss({ modalId, hideCloseButton: !allowDismiss })}</style>
            <Modal
                size="large"
                id={modalId}
                data-testid={modalId}
                show={show}
                header="Confirmation"
                footer={{
                    primary: {
                        buttonStyle: 'primary',
                        action: handleProceed,
                        label: proceedLabel
                    },
                    options: allowDismiss ? [cancelOption, programsOption] : [programsOption]
                }}
                // Interstate renders the close control whenever size is "large", so the absent handler
                // is what makes it and the backdrop inert.
                onHide={allowDismiss ? onCancel : undefined}
            >
                <StyledModalBody>
                    <Typography variant="body-md" data-testid="item-retention-intro-text" sx={{ marginBottom: '1rem' }}>
                        Entered values for finance type, term, down payment, trade-in vehicle, and value will persist. Select which
                        other items to keep or remove on the new vehicle.
                    </Typography>

                    <StyledVehicleComparison>
                        <VehicleCard vehicle={currentVehicle} badge="current" />
                        <StyledArrowSeparator>
                            <ArrowLongRightIcon />
                        </StyledArrowSeparator>
                        <VehicleCard vehicle={newVehicle} badge="new" />
                    </StyledVehicleComparison>

                    {categories.length > 0 && (
                        <>
                            <StyledDealDetailsSeparator />
                            <StyledDealDetailsHeader>
                                <Typography variant="h4" tag="span">
                                    Deal Details
                                </Typography>
                                <Box display="flex" alignItems="center" gap="0.5rem">
                                    <Box onClick={handleRetentionToggle}>
                                        <Typography
                                            variant="body-sm"
                                            tag="span"
                                            color="sem.color.on-surface.muted"
                                            data-testid="keep-all-label"
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            Keep all
                                        </Typography>
                                    </Box>
                                    <Switch
                                        name="keep-all-toggle"
                                        data-testid="btn-keep-all"
                                        checked={allChecked}
                                        label=""
                                        onClick={handleRetentionToggle}
                                    />
                                </Box>
                            </StyledDealDetailsHeader>

                            {categories.map((category) => (
                                <StyledCategorySection key={category.key} data-testid={`category-${category.key}`}>
                                    <StyledCategoryHeader>
                                        <Typography variant="strong-md" tag="span">
                                            {category.label} ({category.items.length})
                                        </Typography>
                                    </StyledCategoryHeader>

                                    {category.items.map((item) => (
                                        <StyledItemRow key={item.id} data-testid={`item-row-${category.key}-${item.id}`}>
                                            <StyledItemLabel>
                                                <Typography variant="body-sm" tag="span">
                                                    {item.displayText}
                                                </Typography>
                                            </StyledItemLabel>
                                            <StyledKeepCheckboxWrapper>
                                                <CheckBox
                                                    id={`keep-${category.key}-${item.id}`}
                                                    data-testid={`keep-checkbox-${category.key}-${item.id}`}
                                                    label="Keep"
                                                    value={`keep-${item.id}`}
                                                    checked={selections[category.key]?.[item.id] ?? false}
                                                    onChange={(event: any) => {
                                                        handleCheckboxChange(category.key, item.id, event.target.value.checked);
                                                    }}
                                                />
                                            </StyledKeepCheckboxWrapper>
                                        </StyledItemRow>
                                    ))}
                                </StyledCategorySection>
                            ))}
                        </>
                    )}
                </StyledModalBody>
            </Modal>
        </>
    );
};
